import { getPartialItems, getOverQuantityItems } from 'shared/components/CartProvider/helpers';
import { Cart } from 'shared/types/cart';

export type CartQuantityAdjustmentBlocker = 'partialOOS' | 'overQuantity';

const PENDING_POST_LOGIN_PARTIAL_OOS = 'pendingPostLoginPartialOOS';
const PENDING_POST_LOGIN_OVER_QTY_NUDGE = 'pendingPostLoginOverQtyNudge';

export const getCartQuantityAdjustmentBlocker = (
  cart: Cart | undefined,
  isOverQuantityNudgeEnabled: boolean
): CartQuantityAdjustmentBlocker | null => {
  if (getPartialItems(cart).length > 0) {
    return 'partialOOS';
  }
  if (isOverQuantityNudgeEnabled && getOverQuantityItems(cart).length > 0) {
    return 'overQuantity';
  }
  return null;
};

export const setPendingPostLoginPartialOOS = () => {
  sessionStorage.removeItem(PENDING_POST_LOGIN_OVER_QTY_NUDGE);
  sessionStorage.setItem(PENDING_POST_LOGIN_PARTIAL_OOS, '1');
};

export const setPendingPostLoginOverQtyNudge = () => {
  sessionStorage.setItem(PENDING_POST_LOGIN_OVER_QTY_NUDGE, '1');
};

export const hasPendingPostLoginPartialOOS = (): boolean =>
  sessionStorage.getItem(PENDING_POST_LOGIN_PARTIAL_OOS) === '1';

export const hasPendingPostLoginOverQtyNudge = (): boolean =>
  sessionStorage.getItem(PENDING_POST_LOGIN_OVER_QTY_NUDGE) === '1';

export const clearPendingPostLoginPartialOOS = () => {
  sessionStorage.removeItem(PENDING_POST_LOGIN_PARTIAL_OOS);
};

export const clearPendingPostLoginOverQtyNudge = () => {
  sessionStorage.removeItem(PENDING_POST_LOGIN_OVER_QTY_NUDGE);
};

export const clearAllPendingPostLoginQuantityNudges = () => {
  clearPendingPostLoginPartialOOS();
  clearPendingPostLoginOverQtyNudge();
};

interface ShowPostLoginNudgeParams {
  partialOOSItemsLength: number;
  overQuantityItemsLength: number;
  isOverQuantityNudgeEnabled: boolean;
  showPartialOOS: () => void;
  showOverQuantity: () => void;
}

/** Shows the correct post-login sheet; partial OOS wins when both flags/items exist. */
export const showPostLoginQuantityNudge = ({
  partialOOSItemsLength,
  overQuantityItemsLength,
  isOverQuantityNudgeEnabled,
  showPartialOOS,
  showOverQuantity,
}: ShowPostLoginNudgeParams): boolean => {
  if (
    hasPendingPostLoginPartialOOS() &&
    partialOOSItemsLength > 0
  ) {
    clearPendingPostLoginPartialOOS();
    showPartialOOS();
    return true;
  }

  if (
    isOverQuantityNudgeEnabled &&
    hasPendingPostLoginOverQtyNudge() &&
    overQuantityItemsLength > 0 &&
    partialOOSItemsLength === 0
  ) {
    clearPendingPostLoginOverQtyNudge();
    showOverQuantity();
    return true;
  }

  return false;
};

interface BlockCheckoutParams {
  partialOOSItemsLength: number;
  overQuantityItemsLength: number;
  isOverQuantityNudgeEnabled: boolean;
  showPartialOOS: () => void;
  showOverQuantity: () => void;
}

/** Blocks checkout and opens the right sheet; returns true if blocked. */
export const blockCheckoutForQuantityAdjustment = ({
  partialOOSItemsLength,
  overQuantityItemsLength,
  isOverQuantityNudgeEnabled,
  showPartialOOS,
  showOverQuantity,
}: BlockCheckoutParams): boolean => {
  if (partialOOSItemsLength > 0) {
    showPartialOOS();
    return true;
  }

  if (isOverQuantityNudgeEnabled && overQuantityItemsLength > 0) {
    showOverQuantity();
    return true;
  }

  return false;
};

interface ResolveCartQuantityBlockerParams {
  cart: Cart | undefined;
  isOverQuantityNudgeEnabled: boolean;
  showPartialOOS: () => void;
  showOverQuantity: () => void;
  includePostLoginPending?: boolean;
}


export const resolveCartQuantityAdjustmentBeforeCheckout = ({
  cart,
  isOverQuantityNudgeEnabled,
  showPartialOOS,
  showOverQuantity,
  includePostLoginPending = false,
}: ResolveCartQuantityBlockerParams): boolean => {
  const partialOOSItemsLength = getPartialItems(cart).length;
  const overQuantityItemsLength = getOverQuantityItems(cart).length;

  if (
    includePostLoginPending &&
    showPostLoginQuantityNudge({
      partialOOSItemsLength,
      overQuantityItemsLength,
      isOverQuantityNudgeEnabled,
      showPartialOOS,
      showOverQuantity,
    })
  ) {
    return true;
  }

  return blockCheckoutForQuantityAdjustment({
    partialOOSItemsLength,
    overQuantityItemsLength,
    isOverQuantityNudgeEnabled,
    showPartialOOS,
    showOverQuantity,
  });
};
